import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import paymentAPI from '../services/paymentAPI';

const PRICE_UNLIMITED_MONTHLY = process.env.REACT_APP_STRIPE_PRICE_UNLIMITED_MONTHLY || '';
const PRICE_3_CREDITS = process.env.REACT_APP_STRIPE_PRICE_3_CREDITS || '';
const PRICE_10_CREDITS = process.env.REACT_APP_STRIPE_PRICE_10_CREDITS || '';

const quickOptions = [
  {
    id: 'credits_3',
    priceId: PRICE_3_CREDITS,
    label: '3 Resume Pack',
    price: '$5',
    detail: 'One-time',
    accent: 'var(--color-charcoal-800)',
  },
  {
    id: 'credits_10',
    priceId: PRICE_10_CREDITS,
    label: '10 Resume Pack',
    price: '$15',
    detail: 'One-time • Best value',
    accent: 'var(--valley-emerald)',
  },
  {
    id: 'unlimited_monthly',
    priceId: PRICE_UNLIMITED_MONTHLY,
    label: 'Unlimited Monthly',
    price: '$20',
    detail: 'Monthly subscription',
    accent: 'var(--color-accent-ferrari)',
    isPopular: true,
  },
];

/**
 * UpgradeModal
 *
 * Props:
 *   isOpen       {boolean}  — whether to show the modal
 *   onClose      {function} — called when the user closes the modal
 *   presaveState {object}   — { jd, analysis } to save before Stripe redirect
 */
export default function UpgradeModal({ isOpen, onClose, presaveState = {} }) {
  const navigate = useNavigate();
  const [loadingId, setLoadingId] = useState(null);

  if (!isOpen) return null;

  const handleSelect = async (option) => {
    setLoadingId(option.id);
    try {
      await paymentAPI.redirectToCheckout(option.priceId, presaveState);
    } catch (err) {
      alert(err.message || 'Something went wrong. Please try again.');
      setLoadingId(null);
    }
  };

  return (
    <div
      style={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="responsive-modal" style={styles.modal}>
        {/* Close button */}
        <button
          id="upgrade-modal-close-btn"
          onClick={onClose}
          style={styles.closeBtn}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Icon */}
        <div style={styles.iconWrap} aria-hidden="true">
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
            <circle cx="22" cy="22" r="22" fill="rgba(79,70,229,0.1)" />
            <path d="M22 14v8M14 30c0-4 3.6-7 8-7s8 3 8 7" stroke="var(--color-accent-ferrari)" strokeWidth="2" strokeLinecap="round" />
            <circle cx="22" cy="17" r="3" stroke="var(--color-accent-ferrari)" strokeWidth="2" />
          </svg>
        </div>

        <h2 id="upgrade-modal-title" style={styles.title}>Out of optimizations</h2>
        <p style={styles.body}>
          You've used your free credit. Choose a plan below to keep going or view all plans for details.
        </p>

        {/* Quick plan options */}
        <div style={styles.optionList} role="list">
          {quickOptions.map((opt) => (
            <button
              key={opt.id}
              id={`upgrade-modal-${opt.id}-btn`}
              onClick={() => handleSelect(opt)}
              disabled={!!loadingId}
              role="listitem"
              style={{
                ...styles.optionBtn,
                borderColor: opt.isPopular ? 'var(--color-accent-ferrari)' : 'var(--color-border)',
                background: opt.isPopular ? 'rgba(79,70,229,0.04)' : 'transparent',
              }}
            >
              <div style={styles.optionLeft}>
                <span style={{ ...styles.optionLabel, color: opt.accent }}>{opt.label}</span>
                <span style={styles.optionDetail}>{opt.detail}</span>
              </div>
              <div style={styles.optionRight}>
                <span style={styles.optionPrice}>{opt.price}</span>
                {loadingId === opt.id
                  ? <span style={styles.spinner} />
                  : <span style={{ ...styles.optionArrow, color: opt.accent }}>→</span>}
              </div>
              {opt.isPopular && <span style={styles.popularPill}>Popular</span>}
            </button>
          ))}
        </div>

        <button
          id="upgrade-modal-view-all-btn"
          onClick={() => navigate('/pricing')}
          style={styles.viewAllBtn}
        >
          View all plans & details →
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.55)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px',
    animation: 'rz-fadeInFlat 0.2s ease',
  },
  modal: {
    background: 'rgba(255,255,255,0.97)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid rgba(226,232,240,0.8)',
    borderRadius: 'var(--radius-xl)',
    padding: '40px 36px',
    maxWidth: 420,
    width: '100%',
    position: 'relative',
    textAlign: 'center',
    boxShadow: 'var(--shadow-xl)',
    animation: 'rz-fadeIn 0.3s cubic-bezier(0.16,1,0.3,1)',
  },
  closeBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    background: 'none',
    border: 'none',
    color: 'var(--color-text-muted)',
    fontSize: 18,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 'var(--radius-sm)',
    lineHeight: 1,
    transition: 'color var(--transition-fast)',
  },
  iconWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.4rem',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: 'var(--color-text-primary)',
    marginBottom: 10,
  },
  body: {
    fontSize: '0.9rem',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6,
    marginBottom: 24,
  },
  optionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginBottom: 20,
    textAlign: 'left',
  },
  optionBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    border: '1.5px solid',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
    position: 'relative',
    fontFamily: 'var(--font-body)',
  },
  optionLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  optionLabel: {
    fontFamily: 'var(--font-heading)',
    fontWeight: 700,
    fontSize: '0.95rem',
  },
  optionDetail: {
    fontSize: '0.78rem',
    color: 'var(--color-text-muted)',
  },
  optionRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  optionPrice: {
    fontFamily: 'var(--font-heading)',
    fontWeight: 800,
    fontSize: '1.1rem',
    color: 'var(--color-text-primary)',
  },
  optionArrow: {
    fontSize: '1.1rem',
    fontWeight: 700,
  },
  popularPill: {
    position: 'absolute',
    top: -8,
    right: 12,
    background: 'var(--color-accent-ferrari)',
    color: '#fff',
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    padding: '2px 8px',
    borderRadius: 'var(--radius-full)',
  },
  spinner: {
    display: 'inline-block',
    width: 16,
    height: 16,
    border: '2px solid currentColor',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'rz-spin 0.7s linear infinite',
  },
  viewAllBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--color-accent-ferrari)',
    fontFamily: 'var(--font-heading)',
    fontWeight: 600,
    fontSize: '0.88rem',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: 'var(--radius-sm)',
    width: '100%',
    transition: 'opacity var(--transition-fast)',
  },
};
